import { useEffect, useMemo, useRef, useState } from 'react';
import { useGlobal } from '../../context/GlobalContext';
import { formatPriceText, toNumericPriceValue } from '../../lib/currency';
import { isEnglishLocale } from '../../lib/locale';
import InlineAvailabilitySvg from './InlineAvailabilitySvg';
import {
  AVAILABILITY_ASSETS,
  buildQuoteUrl,
  getDepartmentDetailUrl,
  getFloorImage,
  getFloorSvg,
} from './availabilityConfig';
import {
  normalizeDepartmentDetail,
  normalizeKey,
  toAvailabilityItem,
} from './availabilityData';
import styles from './AvailabilityList.module.css';

const Chevron = ({ open = false }) => (
  <svg
    aria-hidden="true"
    className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
    viewBox="0 0 20 20"
  >
    <path d="M5 7.5 10 12.5l5-5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

const ArrowBack = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20">
    <path d="M16 10H4m5-5-5 5 5 5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

const MediaImage = ({ src, alt, className = '' }) => {
  const [failedSrc, setFailedSrc] = useState('');
  const failed = failedSrc === src;

  if (!src || failed) {
    return (
      <div className={`${styles.mediaPlaceholder} ${className}`}>
        <span>Imagen oficial pendiente</span>
        <small>{src}</small>
      </div>
    );
  }

  return <img className={className} src={src} alt={alt} onError={() => setFailedSrc(src)} />;
};

function getDisplayName(item, isEn) {
  const raw = String(item?.title || '').trim();
  if (/departamento|depto|department/i.test(raw)) return raw;
  return `${isEn ? 'Department' : 'Departamento'} ${item?.number || raw}`;
}

function displayFeatureLabel(value) {
  const text = String(value || '').replace(/[_-]+/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';
}

function getPrimaryFeature(item) {
  return item.summary.find((feature) => {
    const key = normalizeKey(feature?.atributo);
    return key.includes('tipo') || key.includes('m2') || key.includes('metro');
  }) || item.summary[0];
}

function formatServicePrice(value, currency, isEn) {
  const numericValue = toNumericPriceValue(value);
  const currencyCode = /^[A-Z]{3}$/.test(currency) ? currency : 'MXN';
  if (!Number.isFinite(numericValue)) return '—';

  const formatted = new Intl.NumberFormat(isEn ? 'en-US' : 'es-MX', {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);

  return `${formatted} ${currencyCode}`;
}

const FractionCards = ({ detailState, department, floor, isEn }) => {
  const fractions = detailState.data?.fractions || [];
  const enabledCount = fractions.filter((fraction) => fraction.enabled).length;

  if (detailState.loading) {
    return (
      <p className={styles.feedback} role="status">
        <span className={styles.loader} aria-hidden="true" />
        Consultando fracciones…
      </p>
    );
  }
  if (detailState.error) {
    return <p className={styles.feedbackError}>No fue posible consultar las fracciones: {detailState.error}</p>;
  }
  if (!fractions.length) return <p className={styles.feedback}>El servicio no devolvió fracciones para este departamento.</p>;

  return (
    <section className={styles.fractionSection}>
      <h3>{enabledCount} {isEn ? 'fractions available' : 'fracciones disponibles'}</h3>
      <div className={styles.fractionGrid}>
        {fractions.map((fraction) => {
          const quoteUrl = buildQuoteUrl({
            department,
            fraction: fraction.number,
            floor,
            price: fraction.price,
          });
          const dateText = [fraction.startDate, fraction.endDate].filter(Boolean).join(' – ');

          return (
            <article className={`${styles.fractionCard} ${!fraction.enabled ? styles.fractionDisabled : ''}`} key={fraction.id}>
              <span className={styles.fractionStatus}>
                <i
                  aria-hidden="true"
                  style={{ backgroundColor: fraction.color || 'var(--availability-blue)' }}
                />
                {fraction.status}
              </span>
              <strong>Fracción {fraction.number}</strong>
              <span>{fraction.info}</span>
              {fraction.facts?.length ? (
                <dl className={styles.fractionFacts}>
                  {fraction.facts.map((fact) => (
                    <div key={`${fact.label}-${fact.value}`}>
                      <dt>{displayFeatureLabel(fact.label)}</dt>
                      <dd>{fact.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              <footer>
                {fraction.enabled ? (
                  <div className={styles.fractionPrice}>
                    <b>{formatServicePrice(fraction.price, fraction.currency, isEn)}</b>
                    <small>{dateText || '—'}</small>
                  </div>
                ) : <span />}
                {fraction.enabled ? (
                  quoteUrl ? (
                    <a href={quoteUrl} target="_blank" rel="noopener noreferrer">
                      {isEn ? 'Quote' : 'Cotizar'}
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      title={isEn ? 'Quote URL pending' : 'URL del cotizador pendiente'}
                    >
                      {isEn ? 'Quote' : 'Cotizar'}
                    </button>
                  )
                ) : null}
              </footer>
            </article>
          );
        })}
      </div>
    </section>
  );
};

const AvailabilityList = ({ data, items = [], pageTitulo = '' }) => {
  const { companyInfo, locale } = useGlobal();
  const isEn = isEnglishLocale(locale);
  const [phase, setPhase] = useState('building');
  const [selectedFloor, setSelectedFloor] = useState('');
  const [selectedNumber, setSelectedNumber] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [detailState, setDetailState] = useState({ number: '', loading: false, error: '', data: null });
  const listRef = useRef(null);
  const itemRefs = useRef(new Map());

  const normalizedItems = useMemo(
    () => items.map(toAvailabilityItem).filter((item) => item.number),
    [items]
  );

  const floors = useMemo(
    () => [...new Set(normalizedItems.map((item) => item.floor).filter(Boolean))]
      .sort((a, b) => Number(a) - Number(b)),
    [normalizedItems]
  );

  const floorItems = useMemo(() => {
    const filtered = selectedFloor
      ? normalizedItems.filter((item) => String(Number(item.floor)) === String(Number(selectedFloor)))
      : normalizedItems;

    return [...filtered].sort((a, b) => {
      const delta = Number(a.number) - Number(b.number);
      return sortDirection === 'asc' ? delta : -delta;
    });
  }, [normalizedItems, selectedFloor, sortDirection]);

  const unitColors = useMemo(
    () => Object.fromEntries(floorItems.map((item) => [String(Number(item.number)), item.color]).filter(([, color]) => color)),
    [floorItems]
  );

  useEffect(() => {
    if (phase !== 'floor' || !expandedId || !selectedNumber) return undefined;

    const controller = new AbortController();
    fetch(getDepartmentDetailUrl(selectedNumber), {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || `Error ${response.status}`);
        return normalizeDepartmentDetail(payload);
      })
      .then((detail) => setDetailState({ number: selectedNumber, loading: false, error: '', data: detail }))
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setDetailState({ number: selectedNumber, loading: false, error: error.message, data: null });
        }
      });

    return () => controller.abort();
  }, [expandedId, phase, selectedNumber]);

  if (!normalizedItems.length) return null;

  const disclaimer = typeof data?.disclaimer === 'string' && data.disclaimer.trim()
    ? data.disclaimer.trim()
    : (isEn
      ? '*Prices and information are subject to change without prior notice.'
      : '*Los precios e información están sujetos a cambios sin previo aviso.');

  const formatPrice = (value) => formatPriceText(value, companyInfo, locale) || '—';

  const expandItem = (item) => {
    setSelectedNumber(item.number);
    setExpandedId(item.id);
    setDetailState({ number: item.number, loading: true, error: '', data: null });
  };

  const selectFloor = (floor, unit = '') => {
    const value = String(Number(floor));
    if (!value || value === 'NaN') return;
    setSelectedFloor(value);
    setPhase('floor');

    const item = unit
      ? normalizedItems.find((candidate) => String(Number(candidate.number)) === String(Number(unit)))
      : null;
    if (item) expandItem(item);
    else {
      setSelectedNumber('');
      setExpandedId(null);
      setDetailState({ number: '', loading: false, error: '', data: null });
    }
  };

  const selectUnitOnMap = (number) => {
    const item = floorItems.find((candidate) => String(Number(candidate.number)) === String(Number(number)));
    if (!item) return;

    expandItem(item);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const list = listRef.current;
        const itemNode = itemRefs.current.get(item.id);
        if (!list || !itemNode) return;

        const hasInternalScroll = list.scrollHeight > list.clientHeight + 1;
        if (!hasInternalScroll) {
          itemNode.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          return;
        }

        const listRect = list.getBoundingClientRect();
        const itemRect = itemNode.getBoundingClientRect();
        const targetTop = list.scrollTop
          + itemRect.top
          - listRect.top;

        list.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      });
    });
  };

  const toggleItem = (item) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      setDetailState({ number: '', loading: false, error: '', data: null });
      return;
    }
    expandItem(item);
  };

  const goBack = () => {
    setPhase('building');
    setSelectedFloor('');
    setSelectedNumber('');
    setExpandedId(null);
    setDetailState({ number: '', loading: false, error: '', data: null });
  };

  const renderSortButton = () => (
    <button
      className={styles.sortButton}
      type="button"
      aria-label={isEn ? 'Change order' : 'Cambiar orden'}
      onClick={() => setSortDirection((current) => current === 'asc' ? 'desc' : 'asc')}
    >
      <span className={sortDirection === 'asc' ? styles.sortActive : ''}>⌃</span>
      <span className={sortDirection === 'desc' ? styles.sortActive : ''}>⌄</span>
    </button>
  );

  const visibleItems = phase === 'building' ? normalizedItems : floorItems;

  return (
    <section className={styles.availability} aria-label={data?.titulo || pageTitulo || 'Disponibilidad'}>
      <div className={styles.panel}>
        <header className={styles.panelHeader}>
          <h2>
            {phase === 'floor' ? floorItems.length : normalizedItems.length}{' '}
            {isEn ? 'departments found' : 'departamentos encontrados'}
          </h2>
          {phase === 'floor' ? (
            <button className={styles.backButton} type="button" onClick={goBack}>
              <ArrowBack /> {isEn ? 'Back' : 'Atrás'}
            </button>
          ) : null}
        </header>

        <div className={styles.panelContent}>
          <div className={styles.tableHeader}>
            <span>{isEn ? 'Name' : 'Nombre'} {renderSortButton()}</span>
            <span className={styles.generalPrice}>{isEn ? 'Price' : 'Precio'} {renderSortButton()}</span>
            <span aria-hidden="true" />
          </div>
          <div ref={listRef} className={styles.list}>
            {visibleItems.map((item) => {
              const isExpanded = expandedId === item.id;
              const primaryFeature = getPrimaryFeature(item);
              const itemDetail = detailState.number === item.number
                ? detailState
                : { number: item.number, loading: true, error: '', data: null };

              return (
                <article
                  ref={(node) => {
                    if (node) itemRefs.current.set(item.id, node);
                    else itemRefs.current.delete(item.id);
                  }}
                  className={`${styles.item} ${isExpanded ? styles.itemExpanded : ''}`}
                  key={item.id}
                >
                  <button
                    className={styles.itemRow}
                    type="button"
                    onClick={() => phase === 'building' ? selectFloor(item.floor, item.number) : toggleItem(item)}
                  >
                    <span>{getDisplayName(item, isEn)}</span>
                    <span className={styles.generalPrice}>{formatPrice(item.price)}</span>
                    <span className={styles.rowChevron}><Chevron open={isExpanded} /></span>
                  </button>
                  {phase === 'floor' && isExpanded ? (
                    <div className={styles.itemDetail}>
                      <div className={styles.detailGrid}>
                        {primaryFeature ? (
                          <div>
                            <strong>{displayFeatureLabel(primaryFeature.atributo)}</strong>
                            <span>{primaryFeature.valor || '—'}</span>
                          </div>
                        ) : null}
                        {item.summary.filter((feature) => feature !== primaryFeature).map((feature) => (
                          <div key={`${feature.atributo}-${feature.valor}`}>
                            <strong>{displayFeatureLabel(feature.atributo)}</strong>
                            <span>{feature.valor || '—'}</span>
                          </div>
                        ))}
                      </div>
                      <FractionCards
                        detailState={itemDetail}
                        department={item.number}
                        floor={item.floor}
                        isEn={isEn}
                      />
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>

        <p className={styles.disclaimer}>{disclaimer}</p>
      </div>

      <div className={styles.visual}>
        {phase === 'building' ? (
          <div className={styles.buildingCanvas}>
            <MediaImage className={styles.backgroundImage} src={AVAILABILITY_ASSETS.buildingImage} alt="Edificio SLS Noma" />
            <InlineAvailabilitySvg
              className={`${styles.svgHost} ${styles.buildingSvg}`}
              src={AVAILABILITY_ASSETS.buildingSvg}
              mode="building"
              availableValues={floors}
              onSelect={selectFloor}
            />
          </div>
        ) : (
          <>
            <div className={styles.floorCanvas}>
              <MediaImage className={styles.backgroundImage} src={getFloorImage(selectedFloor)} alt={`Planta del piso ${selectedFloor}`} />
              <InlineAvailabilitySvg
                className={`${styles.svgHost} ${styles.floorSvg}`}
                src={getFloorSvg(selectedFloor)}
                mode="floor"
                availableValues={floorItems.map((item) => item.number)}
                selectedValue={selectedNumber}
                colorMap={unitColors}
                onSelect={selectUnitOnMap}
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default AvailabilityList;
